import useAuth from 'hooks/useAuth'
import { useUserData } from 'hooks/users'
import useConfiguration from 'hooks/useConfiguration'
import { PRIVILEGES, DIGITAL_MAIL_EDITABLE_METADATA } from 'utils/constants'
import { useCallback } from 'react'

/**
 * use this hook for instance-specific data rendering
 */
const useInstance = () => {
  const {
    user: { instanceId, isMPXAdmin },
  } = useAuth()
  const { hasPrivilege, currentModule, instanceConfigurations } =
    useConfiguration()
  const { formatUserTimeZone } = useUserData()

  const metadataColumns = Object.entries(
    instanceConfigurations[DIGITAL_MAIL_EDITABLE_METADATA] || {}
  ).map(([key, value]) => ({
    id: key,
    ...value,
  }))

  const alterDocumentInboundItem = async (documentInboundItem) => {
    const fieldDefinitions = currentModule?.configurations?.find(
      (config) => config.configuration_type === 'field_definitions'
    )
    const fieldSets = fieldDefinitions?.fieldsets || []
    const alteredDocumentInboundItem = { ...documentInboundItem }

    if (fieldDefinitions) {
      try {
        fieldSets.forEach((fieldset) => {
          if (typeof fieldset.fields === 'object') {
            Object.entries(fieldset.fields).forEach(([_, value]) => {
              alteredDocumentInboundItem[value.field] =
                documentInboundItem._embedded.metadata[value.field]
            })
          }
        })
      } catch (err) {
        // continue
      }
    }

    if (Array.isArray(metadataColumns)) {
      metadataColumns
        .filter((col) => col.view === 'list')
        .forEach((col) => {
          if (col.id === 'file_size') return

          alteredDocumentInboundItem[col.id] =
            documentInboundItem._embedded.metadata?.[col.id]
        })
    }

    return alteredDocumentInboundItem
  }

  const alterDocumentInboundItems = async (documentInboundItems) => {
    if (
      !Array.isArray(documentInboundItems) ||
      documentInboundItems.length === 0
    )
      return documentInboundItems

    if (metadataColumns.length > 0) {
      // append specific metadata cols to documentInboundItems
      return Promise.all(documentInboundItems.map(alterDocumentInboundItem))
    }

    return documentInboundItems
  }

  const getDigitalMailDisplayDocument = (document) => {
    if (!document) return {}

    const displayDocument = {
      status: document.status ?? 'N/A',
      category: document.category,
      assigned_to: document.assigned_to_display_name ?? 'N/A',
      type: 'GENERAL',
      account_number: document.account_id,
      tracking_number: document.tracking_number ?? 'N/A',
      inbound_document_id: document.id ?? 'N/A',
      date_created:
        document._embedded?.document?.datetime_created &&
        formatUserTimeZone(
          new Date(document._embedded?.document?.datetime_created)
        ),
      _embedded: document._embedded,
      file_size: document.file_size || '0B',
    }

    if (instanceId === 'dayforce') {
      displayDocument.envelope_id = document.envelope_id ?? 'N/A'
    }

    const fieldDefinitions = currentModule?.configurations?.find(
      (config) => config.configuration_type === 'field_definitions'
    )
    const fieldSets = fieldDefinitions?.fieldsets || []

    fieldSets.forEach((fieldset) => {
      if (typeof fieldset.fields === 'object') {
        Object.entries(fieldset.fields).forEach(([_, value]) => {
          displayDocument[value.field] = document[value.field]
        })
      }
    })

    if (Array.isArray(metadataColumns)) {
      metadataColumns.forEach((col) => {
        if (col.view === 'admin' && !isMPXAdmin) {
          delete displayDocument[col.id]
          return
        }

        if (col.id === 'file_size') return

        displayDocument[col.id] = document?._embedded?.metadata?.[col.id]

        if (instanceId === 'chubb') {
          if (col.id === 'DateCheckShipped' && document?._embedded?.document) {
            displayDocument[col.id] =
              document._embedded.document?.datetime_mailed
          }

          if (col.id === 'DepositDate' && document?._embedded?.document) {
            displayDocument[col.id] =
              document._embedded.document?.datetime_mailed
          }

          if (
            col.id === 'DateCheckPulled' &&
            document?._embedded?.pull_request &&
            // Only show the date if PulledOrShippedTracking is present
            document?._embedded?.metadata?.['PulledOrShippedTracking']
          ) {
            displayDocument[col.id] =
              document._embedded.pull_request?.datetime_created
          }
        }
      })
    }

    return displayDocument
  }

  const getDigitalMailFilters = () => {
    const filters = [
      'document_id',
      'account_id',
      'category',
      'status',
      'date_doc',
      'tracking_number',
    ]

    const canFilterUsers = hasPrivilege(PRIVILEGES.VIEW_ALL_ITEMS)

    if (canFilterUsers) {
      filters.push('assigned_to_user_id')
    }

    if (instanceId === 'evolentmpc') {
      filters.push('MemberID')
    }

    return filters
  }

  const getDigitalMailEditableColumns = () => {
    let doEdit = [
      'status',
      'category',
      'assigned_to',
      'account_number',
      'tracking_number',
      'MemberID',
      'Drawer',
      'FileNumber',
      'FolderNumber',
      'DocumentType',
      'PageDescription',
    ]

    let editableWhenEmpty = [
      'tracking_number',
      'OriginalDocument',
      'PhysicalItemDescription',
    ]

    if (instanceId === 'dayforce') {
      doEdit.push('envelope_id')
    }

    return { doEdit, editableWhenEmpty }
  }

  const getDigitalMailVisibleColumns = useCallback(() => {
    const visibleColumns = [
      'document_id',
      'account_id',
      'category',
      'status',
      'date_doc',
      'assigned_to_display_name',
      'tracking_number',
    ]

    if (Array.isArray(metadataColumns)) {
      visibleColumns.push(
        ...metadataColumns
          .filter((col) => col.view === 'list')
          .sort((a, b) => (a.position ?? 99) - (b.position ?? 99))
          .map((col) => col.id)
      )
    }

    return visibleColumns
  }, [metadataColumns])

  const getMailOriginalCustomAddresses = () => {
    // display address and update notes
    let customAddressIds = {
      return_to_sender: 'RETURN TO SENDER ',
    }

    // do not display address
    let nonAddressIds = {
      destroy: 'DESTROY ',
    }

    if (instanceId === 'chubb') {
      customAddressIds = {
        overnight: 'OVERNIGHT',
      }

      nonAddressIds = {
        void_check: 'VOID/DESTROY',
        chubb_deposit_to_citi: 'CHUBB DEPOSIT TO CITI ',
        healthy_paws_deposit_to_boa: 'Healthy Paws Deposit to BOA ',
        total_loss_evidence_locker: 'TOTAL LOSS - EVIDENCE LOCKER ',
      }
    } else if (instanceId === 'dayforce') {
      customAddressIds = {
        overnight: 'OVERNIGHT',
      }

      nonAddressIds = {
        destroy: 'DESTROY',
        deposit_to_bank: 'DEPOSIT TO BANK',
      }
    }

    return { nonAddressIds, customAddressIds }
  }

  const showDigitalMailDetailsItem = ({ values, id, document }) => {
    if (instanceId === 'chubb') {
      const col = instanceConfigurations?.[DIGITAL_MAIL_EDITABLE_METADATA]?.[id]

      if (!col) return true

      if (Array.isArray(col.categories) && col.categories.length > 0) {
        const show = col.categories.includes(values.category?.toUpperCase())

        if (show && id === 'DepositDate') {
          return document?.lot_id === 3
        }

        return show
      }
    } else if (instanceId === 'Memic') {
      if (
        id === 'Drawer' ||
        id === 'FolderNumber' ||
        id === 'FileNumber' ||
        id === 'DocumentType' ||
        id === 'PageDescription'
      ) {
        switch (values.category?.toUpperCase()) {
          case 'PREMIUM ACCOUNTING':
          case 'PREMIUM AUDIT':
          case 'FINANCE':
          case 'UNDERWRITING':
          case 'LOSS CONTROL':
          case 'HUMAN RESOURCES':
          case '11409 RETURNED CHECKS':
          case '11409 RETURNED MAIL':
          case 'CASCO VIEW HOLDINGS CHECKS':
          case 'GENERAL MAIL':
          case 'CORRESPONDENCE - MIKE BOURQUE':
          case '11409 CLAIMS':
          case 'PAYMENT CHECKS':
            return false
          default:
            break
        }
      }
    }

    return true
  }

  const getCollateralAccountNumberLabel = ({ requireAccountNumber }) => {
    let label = 'Account Number'

    if (instanceId === 'metrocreditunion') {
      label = 'Account or Person #'
    }

    return `${label} ${requireAccountNumber ? '' : '(Optional)'}`
  }

  const alterCorrespondenceItem = (item) => {
    // always want to display this data, don't really care for the code here. But the code is the search call
    item['_.return_data.scan_code'] = item?._embedded?.return_data?.reason

    if (instanceId === 'chubb') {
      item['datetime_returned'] = item?._embedded?.return_data?.datetime
    }

    return item
  }

  return {
    alterDocumentInboundItem,
    alterDocumentInboundItems,
    getDigitalMailDisplayDocument,
    getDigitalMailFilters,
    getDigitalMailVisibleColumns,
    getDigitalMailEditableColumns,
    getMailOriginalCustomAddresses,
    showDigitalMailDetailsItem,
    getCollateralAccountNumberLabel,
    alterCorrespondenceItem,
  }
}

export default useInstance
